"use client";

import { CLink } from "@/components/common/customLink/CustomLink";
import Button from "@/components/ui/button/Button";
import { IMarqueeSettings } from "@/types/brandLogoGrid";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { BrandLogoSkeleton } from "./BrandLogoSkeleton";

interface Brand {
  _key: string;
  logo?: { url: string; altText?: string };
  link?: { url?: string };
}

interface LogoMarqueeProps {
  brands: Brand[];
  marqueeSettings?: IMarqueeSettings;
  viewAllLabel?: string;
  viewAllHref?: string;
  locale: string;
}

export default function LogoMarquee({
  brands,
  marqueeSettings,
  viewAllLabel,
  viewAllHref = "",
  locale,
}: LogoMarqueeProps) {
  const {
    animationSpeed = 1,
    pauseOnHover = true,
    topRowDirection: rawTopRowDirection = "left",
    bottomRowDirection: rawBottomRowDirection = "right",
    randomizeLogos = true,
    itemSpacing = 40,
  } = marqueeSettings || {};

  const isRTL = locale === "ar";

  // In RTL swap logical directions so the animation remains natural.
  const topRowDirection =
    isRTL && rawTopRowDirection === "left"
      ? "right"
      : isRTL && rawTopRowDirection === "right"
        ? "left"
        : rawTopRowDirection;
  const bottomRowDirection =
    isRTL && rawBottomRowDirection === "left"
      ? "right"
      : isRTL && rawBottomRowDirection === "right"
        ? "left"
        : rawBottomRowDirection;

  const [rows, setRows] = useState<{ top: Brand[]; bottom: Brand[] }>({
    top: [],
    bottom: [],
  });

  useEffect(() => {
    const shuffleLogos = () => {
      let shuffled = [...brands];
      if (randomizeLogos) {
        shuffled = [...shuffled].sort(() => 0.5 - Math.random());
      }
      const middle = Math.ceil(shuffled.length / 2);
      setRows({
        top: shuffled.slice(0, middle),
        bottom: shuffled.slice(middle),
      });
    };

    const timer = setTimeout(shuffleLogos, 0);
    return () => clearTimeout(timer);
  }, [brands, randomizeLogos]);

  if (rows.top.length === 0 && rows.bottom.length === 0) return null;

  return (
    <div key={locale} className="w-full flex flex-col gap-2 md:gap-6">
      {/* Top Row */}
      <MarqueeRow
        items={rows.top}
        direction={topRowDirection}
        speed={animationSpeed}
        pauseOnHover={pauseOnHover}
        spacing={itemSpacing}
      />

      {/* Bottom Row */}
      <MarqueeRow
        items={rows.bottom}
        direction={bottomRowDirection}
        speed={animationSpeed}
        pauseOnHover={pauseOnHover}
        spacing={itemSpacing}
      />

      {/* View All Button — only rendered when a CMS link is configured */}
      {viewAllHref && (
        <CLink
          href={viewAllHref}
          className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9"
        >
          <Button
            variant="contained"
            className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
          >
            {viewAllLabel || (locale === "ar" ? "عرض الكل" : "View All")}
          </Button>
        </CLink>
      )}
    </div>
  );
}

/* ─── Marquee Row ──────────────────────────────────────────────────────────── */

interface MarqueeRowProps {
  items: Brand[];
  direction: "left" | "right";
  speed: number;
  pauseOnHover: boolean;
  spacing: number;
}

function MarqueeRow({ items, direction, speed, pauseOnHover, spacing }: MarqueeRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const scrollPositionRef = useRef(0);
  const animationIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!scrollRef.current || items.length === 0) return;

    const scrollContainer = scrollRef.current;

    const animate = () => {
      if (!isPaused && scrollContainer) {
        const moveSpeed = direction === "left" ? speed : -speed;
        scrollPositionRef.current += moveSpeed;

        // We duplicate items 3× so maxScroll = 1/3 of total scroll width
        const maxScroll = scrollContainer.scrollWidth / 3;

        if (direction === "left") {
          if (scrollPositionRef.current >= maxScroll) scrollPositionRef.current = 0;
        } else {
          if (scrollPositionRef.current <= 0) scrollPositionRef.current = maxScroll;
        }

        scrollContainer.style.transform = `translateX(${-scrollPositionRef.current}px)`;
      }
      animationIdRef.current = requestAnimationFrame(animate);
    };

    animationIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
    };
  }, [direction, isPaused, speed, items.length]);

  if (items.length === 0) return null;

  return (
    <div
      dir="ltr"
      className="relative overflow-hidden w-full py-2"
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
    >
      <div
        ref={scrollRef}
        className="flex items-center"
        style={{
          gap: `${spacing}px`,
          width: "fit-content",
          willChange: "transform",
        }}
      >
        {/* Triplicate items for seamless loop */}
        {[...items, ...items, ...items].map((brand, idx) => (
          <MarqueeLogoItem key={`${brand._key}-${idx}`} brand={brand} />
        ))}
      </div>
    </div>
  );
}

/* ─── Individual Logo Cell ─────────────────────────────────────────────────── */

function MarqueeLogoItem({ brand }: { brand: Brand }) {
  const logoSrc = brand.logo?.url || null;
  const [loaded, setLoaded] = useState(false);

  const content = (
    <div className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-300 flex items-center justify-center h-20 md:h-24 w-32 md:w-40">
      {logoSrc && (
        <div className="relative w-full h-full max-w-[80px] md:max-w-[120px]">
          {!loaded && (
            <BrandLogoSkeleton className="absolute inset-0 w-full h-full rounded-md" />
          )}
          <Image
            src={logoSrc}
            alt="Brand logo"
            fill
            sizes="(max-width: 768px) 80px, 120px"
            className={`object-contain transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
            loading="lazy"
            onLoad={() => setLoaded(true)}
          />
        </div>
      )}
    </div>
  );

  if (brand.link?.url) {
    return (
      <CLink href={brand.link.url} className="block">
        {content}
      </CLink>
    );
  }

  return <div>{content}</div>;
}
